import React, { useState } from 'react';
import { Mail, Calendar, MapPin, Send } from 'lucide-react';
import { PageTransition } from '../components/layout/PageTransition';
import { Button } from '../components/ui/Button';
import { Input, TextArea } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { DecorativeShape } from '../components/DecorativeShape';
import { Animate } from '../components/ui/Animate';
import { useLanguage } from '../context/LanguageContext';
// ============================================================
// CALENDLY: Replace the URL below with your actual Calendly link
// Example: 'https://calendly.com/helgi/15min'
// ============================================================
const CALENDLY_URL = 'https://calendly.com/YOUR-CALENDLY-LINK';
export function Contact() {
  const {
    t
  } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    interest: '',
    message: ''
  });
  const interestOptions = t('contact.interestOptions') as string[];
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      const response = await fetch('https://formsubmit.co/ajax/helgi@helgig.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          interest: formData.interest || interestOptions[0],
          message: formData.message,
          _subject: `New inquiry: ${formData.interest || interestOptions[0]}`
        })
      });
      if (response.ok) {
        setIsSubmitted(true);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          interest: '',
          message: ''
        });
      } else {
        setSubmitError(true);
      }
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleOpenCalendly = () => {
    window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer');
  };
  return <PageTransition data-id="element-213">
      <div className="pt-12 pb-24 relative overflow-hidden" data-id="element-214">
        <DecorativeShape type="blob" color="bg-coral/5" className="top-0 left-0 w-[800px] h-[800px] -translate-x-1/2 -translate-y-1/2 -z-10" data-id="element-215" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-216">
          <Animate className="text-center max-w-2xl mx-auto mb-16" data-id="element-217">
            <h1 className="text-5xl font-bold mb-6" data-id="element-218">
              {'L\xE1tum hlutina gerast \u2728'}
            </h1>
            <p className="text-xl text-gray-600" data-id="element-219">{t('contact.subtitle')}</p>
          </Animate>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-220">
            <div className="space-y-8" data-id="element-221">
              <Animate delay={0.1} data-id="element-222">
                <Card className="p-8 bg-teal text-white relative overflow-hidden" data-id="element-223">
                  <DecorativeShape type="circle" color="bg-white/10" className="bottom-0 right-0 w-48 h-48 translate-x-1/4 translate-y-1/4" data-id="element-224" />
                  <h3 className="text-2xl font-bold mb-6" data-id="element-225">
                    {t('contact.infoTitle')}
                  </h3>
                  <div className="space-y-6" data-id="element-226">
                    <div className="flex items-center gap-4" data-id="element-227">
                      <div className="p-3 bg-white/20 rounded-full" data-id="element-228">
                        <Mail size={24} data-id="element-229" />
                      </div>
                      <div data-id="element-230">
                        <p className="text-teal-100 text-sm" data-id="element-231">
                          {t('contact.emailLabel')}
                        </p>
                        <a href="mailto:helgi@helgig.com" className="font-bold text-lg hover:underline" data-id="element-232">
                          helgi@helgig.com
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center gap-4" data-id="element-233">
                      <div className="p-3 bg-white/20 rounded-full" data-id="element-234">
                        <MapPin size={24} data-id="element-235" />
                      </div>
                      <div data-id="element-236">
                        <p className="text-teal-100 text-sm" data-id="element-237">
                          {t('contact.locationLabel')}
                        </p>
                        <p className="font-bold text-lg" data-id="element-238">
                          {t('contact.locationValue')}
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              </Animate>

              <Animate delay={0.2} data-id="element-239">
                <Card className="p-8 border-2 border-gray-100" data-id="element-240">
                  <div className="flex items-center gap-4 mb-6" data-id="element-241">
                    <div className="p-3 bg-coral/10 text-coral rounded-full" data-id="element-242">
                      <Calendar size={24} data-id="element-243" />
                    </div>
                    <h3 className="text-2xl font-bold text-charcoal" data-id="element-244">
                      {t('contact.bookCallTitle')}
                    </h3>
                  </div>
                  <p className="text-gray-600 mb-6" data-id="element-245">
                    {t('contact.bookCallDesc')}
                  </p>
                  <Button variant="outline" className="w-full justify-center" onClick={handleOpenCalendly} data-id="element-246">
                    {t('contact.viewCalendar')}
                  </Button>
                </Card>
              </Animate>
            </div>

            <Animate delay={0.15} data-id="element-247">
              <Card className="p-8 md:p-10" data-id="element-248">
                {isSubmitted ? <div className="text-center py-12 animate-fade-in" data-id="element-249">
                    <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-250">
                      <Send size={32} data-id="element-251" />
                    </div>
                    <h3 className="text-2xl font-bold text-charcoal mb-2" data-id="element-252">
                      {t('contact.successTitle')}
                    </h3>
                    <p className="text-gray-600" data-id="element-253">
                      {t('contact.successMessage')}
                    </p>
                    <Button variant="outline" className="mt-8" onClick={() => setIsSubmitted(false)} data-id="element-254">
                      {t('contact.sendAnother')}
                    </Button>
                  </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-255">
                    <h3 className="text-2xl font-bold text-charcoal mb-2" data-id="element-256">
                      {t('contact.formTitle')}
                    </h3>

                    {submitError && <div className="p-4 bg-red-50 text-red-700 rounded-xl text-sm" data-id="element-257">
                        Something went wrong. Please try again or email directly
                        at{' '}
                        <a href="mailto:helgi@helgig.com" className="underline font-bold" data-id="element-258">
                          helgi@helgig.com
                        </a>
                      </div>}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-259">
                      <Input label={t('contact.firstName')} name="firstName" placeholder={t('contact.firstNamePlaceholder')} value={formData.firstName} onChange={handleChange} required data-id="element-260" />
                      <Input label={t('contact.lastName')} name="lastName" placeholder={t('contact.lastNamePlaceholder')} value={formData.lastName} onChange={handleChange} required data-id="element-261" />
                    </div>
                    <Input label={t('contact.email')} name="email" type="email" placeholder={t('contact.emailPlaceholder')} value={formData.email} onChange={handleChange} required data-id="element-262" />
                    <div data-id="element-263">
                      <label className="block text-sm font-bold text-charcoal mb-2 ml-1" data-id="element-264">
                        {t('contact.interestLabel')}
                      </label>
                      <select name="interest" value={formData.interest} onChange={handleChange} className="w-full px-4 py-3 rounded-2xl border-2 border-gray-200 focus:border-teal focus:ring-0 transition-colors outline-none bg-gray-50 focus:bg-white" data-id="element-265">
                        {interestOptions.map((option: string) => <option key={option} value={option} data-id="element-266">
                            {option}
                          </option>)}
                      </select>
                    </div>
                    <TextArea label={t('contact.message')} name="message" rows={4} placeholder={t('contact.messagePlaceholder')} value={formData.message} onChange={handleChange} required data-id="element-267" />
                    <Button type="submit" className="w-full justify-center" size="lg" isLoading={isSubmitting} rightIcon={<Send size={18} data-id="element-269" />} data-id="element-268">
                      {t('contact.send')}
                    </Button>
                  </form>}
              </Card>
            </Animate>
          </div>
        </div>
      </div>
    </PageTransition>;
}